/**
 * Draft → validate → publish, for one site's widget config.
 *
 * One provider per site holds the loaded draft (`base`), the partner's edits
 * (`edited`) and every known issue. Section components read and write
 * `edited` through `update(path, value)`; nothing else mutates it.
 *
 *   validate  client rules (validate.ts) run on every edit; a save is blocked
 *             while any exist.
 *   save      `PUT /config/draft` with only the changed groups. The response
 *             is the NORMALISED document; any field the server rewrote is
 *             reported inline (normalisedFields). 409 CONTRAST_TOO_LOW and a
 *             400 zod body land on their fields the same way.
 *             A save identical to the stored draft answers `unchanged: true`
 *             with that version (no new one): "No changes to save".
 *   publish   saves first when dirty, then `POST /config/publish {version}`.
 *             409 STALE_VERSION (someone saved a newer draft) reloads the
 *             draft rather than publishing over it. With `mirrorToTest`, the
 *             same document is saved and published on the paired test key so
 *             "test a lead" behaves like production.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, type FieldIssue } from '../api/errors';
import { useSession } from '../auth/session';
import { useUnsavedChangesGuard } from '../router';
import {
  CONFIG_GROUPS,
  DEFAULT_CONFIG,
  changedGroups,
  clone,
  draftPatch,
  parseConfig,
  setPath,
  type ConfigGroup,
  type ConfigMeta,
  type LoadedConfig,
  type WidgetConfigModel,
} from './model';
import { normalisedFields, validateConfig } from './validate';

export interface DraftValue {
  siteKey: string;
  loading: boolean;
  loadError: ApiError | null;
  base: WidgetConfigModel;
  edited: WidgetConfigModel;
  draftMeta: ConfigMeta | null;
  publishedMeta: ConfigMeta | null;
  /** The live document (null until loaded; the defaults when nothing is published). */
  published: WidgetConfigModel | null;
  dirtyGroups: ConfigGroup[];
  clientIssues: FieldIssue[];
  serverIssues: FieldIssue[];
  issues: FieldIssue[];
  saving: boolean;
  publishing: boolean;
  actionError: ApiError | null;
  lastSavedVersion: number | null;
  update: (path: string, value: unknown) => void;
  reset: () => void;
  save: () => Promise<boolean>;
  /** Like `save`, but says whether the server stored a new version or found nothing changed. */
  saveOutcome: () => Promise<SaveOutcome>;
  publish: (options?: { mirrorToTest?: boolean }) => Promise<boolean>;
  /** Throws the saved draft (and any unsaved edits) away: back to what is live. */
  discardDraft: () => Promise<boolean>;
  discarding: boolean;
  reload: () => Promise<void>;
}

export type SaveOutcome = 'saved' | 'unchanged' | 'failed';

const DraftContext = createContext<DraftValue | null>(null);

export function ConfigDraftProvider({
  siteKey,
  testSiteKey,
  children,
}: {
  siteKey: string;
  testSiteKey: string | null;
  children: ReactNode;
}) {
  const { api } = useSession();
  const [loaded, setLoaded] = useState<LoadedConfig | null>(null);
  const [publishedMeta, setPublishedMeta] = useState<ConfigMeta | null>(null);
  const [published, setPublished] = useState<WidgetConfigModel | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const [edited, setEdited] = useState<WidgetConfigModel>(clone(DEFAULT_CONFIG));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [serverIssues, setServerIssues] = useState<FieldIssue[]>([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [lastSavedVersion, setLastSavedVersion] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [draft, published] = await Promise.all([
        api.getConfig(siteKey, 'draft'),
        api.getConfig(siteKey, 'published'),
      ]);
      const parsed = parseConfig(draft);
      setLoaded(parsed);
      setEdited(clone(parsed.config));
      const live = parseConfig(published);
      setPublishedMeta(live.meta);
      setPublished(live.config);
      setServerIssues([]);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err : new ApiError({ status: 0, code: 'INTERNAL_ERROR' }));
    } finally {
      setLoading(false);
    }
  }, [api, siteKey]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const base = loaded?.config ?? DEFAULT_CONFIG;
  const dirtyGroups = useMemo(() => changedGroups(base, edited), [base, edited]);
  // Leaving Branding, Widget settings or a setup step with unsaved edits asks first (router.tsx).
  useUnsavedChangesGuard(dirtyGroups.length > 0);
  const clientIssues = useMemo(() => validateConfig(edited), [edited]);

  const update = useCallback((path: string, value: unknown) => {
    setEdited((prev) => setPath(prev, path, value));
    // An edit supersedes whatever the server said about that field.
    setServerIssues((prev) => prev.filter((i) => !(i.path === path || i.path.startsWith(`${path}.`) || path.startsWith(`${i.path}.`))));
  }, []);

  const reset = useCallback(() => {
    setEdited(clone(base));
    setServerIssues([]);
    setActionError(null);
  }, [base]);

  const saveInternal = useCallback(async (): Promise<{ version: number; unchanged: boolean } | null> => {
    if (clientIssues.length) {
      setActionError(new ApiError({ status: 0, code: 'VALIDATION_FAILED', fields: clientIssues }));
      return null;
    }
    const groups = dirtyGroups;
    if (!groups.length) return loaded ? { version: loaded.meta.version, unchanged: true } : null;
    const body = draftPatch(edited, groups, loaded?.extensions);
    try {
      const res = await api.saveDraft(siteKey, body);
      // A no-op save answers with the version it matched, which may be the
      // published one: keep its published flag so the bar does not show an
      // unpublished draft that is not there.
      const stored = parseConfig({ ...res.config, version: res.version, published: res.published ?? false });
      const rewritten = normalisedFields(draftPatch(edited, groups), stored.config);
      setLoaded({ ...stored, extensions: { ...(loaded?.extensions ?? {}), ...stored.extensions } });
      setEdited(clone(stored.config));
      setServerIssues(rewritten);
      setLastSavedVersion(res.version);
      return { version: res.version, unchanged: res.unchanged === true };
    } catch (err) {
      const error = err instanceof ApiError ? err : new ApiError({ status: 0, code: 'INTERNAL_ERROR' });
      setServerIssues(error.fields);
      setActionError(error);
      return null;
    }
  }, [api, clientIssues, dirtyGroups, edited, loaded, siteKey]);

  const saveOutcome = useCallback(async (): Promise<SaveOutcome> => {
    setSaving(true);
    setActionError(null);
    try {
      const res = await saveInternal();
      return res === null ? 'failed' : res.unchanged ? 'unchanged' : 'saved';
    } finally {
      setSaving(false);
    }
  }, [saveInternal]);

  const save = useCallback(async () => (await saveOutcome()) !== 'failed', [saveOutcome]);

  const publish = useCallback(
    async ({ mirrorToTest = true }: { mirrorToTest?: boolean } = {}) => {
      setPublishing(true);
      setActionError(null);
      try {
        const saved = await saveInternal();
        if (saved === null) return false;
        const { version } = saved;
        if (version === 0) {
          // Nothing has ever been saved: publish needs a real row.
          const res = await api.saveDraft(siteKey, draftPatch(edited, CONFIG_GROUPS));
          await api.publish(siteKey, res.version);
        } else {
          await api.publish(siteKey, version);
        }
        if (mirrorToTest && testSiteKey) {
          const mirrored = await api.saveDraft(testSiteKey, draftPatch(edited, CONFIG_GROUPS, loaded?.extensions));
          await api.publish(testSiteKey, mirrored.version);
        }
        await reload();
        return true;
      } catch (err) {
        const error = err instanceof ApiError ? err : new ApiError({ status: 0, code: 'INTERNAL_ERROR' });
        setActionError(error);
        if (error.code === 'STALE_VERSION') await reload();
        return false;
      } finally {
        setPublishing(false);
      }
    },
    [api, edited, loaded?.extensions, reload, saveInternal, siteKey, testSiteKey],
  );

  const discardDraft = useCallback(async () => {
    setDiscarding(true);
    setActionError(null);
    try {
      await api.discardDraft(siteKey);
      await reload();
      return true;
    } catch (err) {
      setActionError(err instanceof ApiError ? err : new ApiError({ status: 0, code: 'INTERNAL_ERROR' }));
      return false;
    } finally {
      setDiscarding(false);
    }
  }, [api, reload, siteKey]);

  const value: DraftValue = {
    siteKey,
    loading,
    loadError,
    base,
    edited,
    draftMeta: loaded?.meta ?? null,
    publishedMeta,
    published,
    dirtyGroups,
    clientIssues,
    serverIssues,
    issues: [...clientIssues, ...serverIssues],
    saving,
    publishing,
    actionError,
    lastSavedVersion,
    update,
    reset,
    save,
    saveOutcome,
    publish,
    discardDraft,
    discarding,
    reload,
  };
  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

/**
 * The setup footer for a step that edits the draft: saves unsaved edits on
 * Continue, and is disabled with "Fix N fields to continue" while the same
 * problems block Save draft and Publish.
 */
export function useDraftContinue(): { beforeContinue: () => Promise<boolean>; continueLabel: string; blockedLabel: string | null } {
  const d = useDraft();
  const n = d.clientIssues.length;
  return {
    beforeContinue: async () => (d.dirtyGroups.length ? d.save() : true),
    continueLabel: d.dirtyGroups.length ? 'Save draft and continue' : 'Continue',
    blockedLabel: n ? `Fix ${n} ${n === 1 ? 'field' : 'fields'} to continue` : null,
  };
}

export function useDraft(): DraftValue {
  const ctx = useContext(DraftContext);
  if (!ctx) throw new Error('useDraft outside ConfigDraftProvider');
  return ctx;
}
